import { mapPayerNameToCodeForDropdown } from "constants/practiceType";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { useFormContext } from "react-hook-form";
import { IPracticeTypeSwitch } from "types/PracticeTypeSuggestion/PracticeTypeSuggestion";

import { requestPracticeTypeRun } from "../practiceTypeRunEvent";
import { usePracticeTypeAutoRerun } from "../usePracticeTypeAutoRerun";

const MODALITY_WORDING: Record<IPracticeTypeSwitch["modality"], string> = {
  MRI: "an MRI",
  CT: "a CT scan",
  ULTRASOUND: "an ultrasound",
  XRAY: "an X-ray",
};

/** The Service Type dropdown's own label for a code; the bare code when the dropdown does not offer it. */
export const practiceTypeName = (code: string): string =>
  mapPayerNameToCodeForDropdown.find((option) => option.value === code)?.name ??
  `Service Type ${code}`;

export const buildPracticeTypeSwitchMessage = (
  practiceTypeSwitch: IPracticeTypeSwitch
): string =>
  `Service Type changed from ${practiceTypeName(
    practiceTypeSwitch.from
  )} to ${practiceTypeName(practiceTypeSwitch.to)} because this order has ${
    MODALITY_WORDING[practiceTypeSwitch.modality] ?? "a procedure"
  }.`;

/**
 * CLE-2971 — "we switched the Service Type", beside that side's Service Type dropdown.
 *
 * The API switches the Service Type inside the eligibility run when the order's CPT codes say so. This
 * reads the switch off the stored response (`benefitDetails(2).response.practiceTypeSwitch`), so it
 * survives a reload and shows on draft / copy / redo pages without any request. Informational, with one
 * way back: "Keep …" re-runs the check under the original code with `keepPracticeType`, after which the
 * response carries no switch and the notice goes away by itself. Hidden once the estimator picks
 * another Service Type by hand: the notice would no longer describe the dropdown.
 *
 * Also hosts the page trigger (`usePracticeTypeAutoRerun`) for its side.
 */
export function PracticeTypeSwitchNotice({
  accountId,
  isSecondaryInsurance = false,
}: {
  accountId: string;
  isSecondaryInsurance?: boolean;
}) {
  usePracticeTypeAutoRerun({ accountId, isSecondaryInsurance });
  const { watch } = useFormContext();
  const benefitDetails = watch(
    isSecondaryInsurance ? "benefitDetails2" : "benefitDetails"
  );
  const practiceType = watch(
    isSecondaryInsurance ? "practiceType2" : "practiceType"
  );
  const practiceTypeSwitch: IPracticeTypeSwitch | null | undefined =
    benefitDetails?.response?.practiceTypeSwitch;

  if (
    !practiceTypeSwitch?.from ||
    !practiceTypeSwitch?.to ||
    practiceType !== practiceTypeSwitch.to
  )
    return null;

  return (
    <div
      className="mb-3"
      data-testid={
        isSecondaryInsurance
          ? "practiceTypeSwitchNotice2"
          : "practiceTypeSwitchNotice"
      }
    >
      <Message
        severity="info"
        content={
          <div className="flex flex-column align-items-start gap-1">
            <span>{buildPracticeTypeSwitchMessage(practiceTypeSwitch)}</span>
            {practiceTypeSwitch.reason && (
              <span className="text-sm">{practiceTypeSwitch.reason}</span>
            )}
            <Button
              type="button"
              label={`Keep ${practiceTypeName(practiceTypeSwitch.from)}`}
              className="p-button-link p-button-sm p-0"
              data-testid="practiceTypeSwitchKeep"
              onClick={() =>
                requestPracticeTypeRun({
                  isSecondaryInsurance,
                  practiceTypeCode: practiceTypeSwitch.from,
                  keepPracticeType: true,
                })
              }
            />
          </div>
        }
      />
    </div>
  );
}
